// Backward + forward scan that lifts a `.`-separated identifier chain
// ending at the cursor into an array of typed parts. Also hosts a small
// set of helpers for building QualifiedName out of those parts.
// Pure function, no tree-sitter.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Dotted-base Resolver.

import type {
  IdentifierQuoting,
  ParseOptions,
  QualifiedName,
  SqlEngine,
  SqlRange,
} from '../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { readIdentifier, foldIdentifier } from '../scope/identifiers';
import type { ReachableTable } from '../types';

export interface DottedPart {
  readonly value: string;   // as-typed identifier (unquoted, escapes resolved)
  readonly folded: string;  // per ParseOptions.identifierFolding / engine default
  readonly quoted: boolean;
  readonly quoting: IdentifierQuoting;
  readonly raw: string;     // source slice (may include quote chars)
  readonly range: SqlRange;
}

export interface DottedChain {
  readonly parts: readonly DottedPart[];
  readonly dblink?: string;              // Oracle `@dblink` suffix
  readonly dblinkRange?: SqlRange;
  readonly chainStart: number;
  readonly chainEnd: number;
  readonly trailingDot: boolean;         // cursor sits immediately after a `.`
}

const IDENT_CONT = /[A-Za-z0-9_$]/;

function isCloseQuoteAt(source: string, pos: number, options: ParseOptions): string | null {
  if (pos < 0) return null;
  const c = source[pos];
  const engine = options.engine;
  if ((engine === 'mysql' || engine === 'sqlite') && c === '`') return '`';
  if ((engine === 'mssql' || engine === 'sqlite') && c === ']') return '[';
  if (c === '"' && engine !== 'mysql') return '"';
  return null;
}

// Walk left from `cursor` through identifier chars, dots, `@` (Oracle), and
// quoted-identifier bodies. Returns the earliest offset that is still part of
// the dotted chain. Does NOT cross whitespace, commas, operators, parens.
export function findDottedChainStart(
  source: string,
  cursor: number,
  options: ParseOptions,
): number {
  let i = cursor;
  while (i > 0) {
    const prev = source[i - 1];
    if (IDENT_CONT.test(prev)) { i--; continue; }
    const closeQuote = isCloseQuoteAt(source, i - 1, options);
    if (closeQuote) {
      const openQuote = closeQuote;
      let j = i - 2;
      while (j >= 0) {
        if (source[j] === openQuote) {
          if (openQuote === '"' && source[j - 1] === '"') { j -= 2; continue; }
          break;
        }
        j--;
      }
      if (j < 0) return i;
      i = j;
      continue;
    }
    if (prev === '.') { i--; continue; }
    if (options.engine === 'oracle' && prev === '@') { i--; continue; }
    // Oracle-only: a leading `:` is part of the chain window so a trigger
    // pseudo-record reference (`:NEW.col` / `:OLD.col`) — and, lexically, any
    // `:bind` variable — keeps its colon. Without this the colon is treated as
    // a chain boundary and the base parses as `NEW`, never matching the
    // Oracle-upper-folded `:NEW` pseudo key. Gated to Oracle so pg/mssql/
    // mysql/sqlite parameter syntaxes are unaffected. A non-trigger `:bind`
    // is inert downstream: it can never match a real schema object (no DB
    // object name starts with `:`), so consumers still return null/empty.
    if (options.engine === 'oracle' && prev === ':') { i--; continue; }
    break;
  }
  return i;
}

// Parse the dotted chain forward from `start` up to (but not past) `cursor`.
// Accepts trailing `.` (cursor immediately after) as a "next part pending"
// flag without emitting an empty part. Oracle `@dblink` is attached to the
// chain (not the parts list) so callers can surface it on QualifiedName.dblink.
export function parseDottedChain(
  source: string,
  start: number,
  cursor: number,
  options: ParseOptions,
): DottedChain | null {
  const lineIdx = buildLineIndex(source);
  const parts: DottedPart[] = [];
  let dblink: string | undefined;
  let dblinkRange: SqlRange | undefined;
  let pos = start;
  let trailingDot = false;

  // Oracle-only: a leading `:` (trigger pseudo-record `:NEW`/`:OLD`, or a
  // `:bind` variable) is not an IDENT_START char, so readIdentifier would
  // skip it and the base would parse as `NEW` — never matching the
  // Oracle-upper-folded `:NEW` pseudo key. Consume the colon here and fold it
  // into the FIRST part (value/folded/raw/range), so `:NEW` folds to `:NEW`
  // (Oracle UPPER, colon preserved). Subsequent parts are unaffected. Gated
  // strictly to Oracle; zero behaviour change for the other dialects.
  const colonStart = options.engine === 'oracle' && source[start] === ':';
  if (colonStart) pos++;

  while (pos < cursor) {
    const ident = readIdentifier(source, pos, cursor, options);
    if (!ident) break;
    const isFirst = parts.length === 0;
    const prependColon = isFirst && colonStart && ident.start === start + 1;
    parts.push({
      value: prependColon ? `:${ident.value}` : ident.value,
      folded: prependColon
        ? `:${foldIdentifier(ident.value, ident.quoted, options)}`
        : foldIdentifier(ident.value, ident.quoted, options),
      quoted: ident.quoted,
      quoting: ident.quoting,
      raw: prependColon ? `:${ident.raw}` : ident.raw,
      range: rangeFromOffsets(lineIdx, prependColon ? start : ident.start, ident.end),
    });
    pos = ident.end;

    if (options.engine === 'oracle' && pos < cursor && source[pos] === '@') {
      pos++;
      const link = readIdentifier(source, pos, cursor, options);
      if (link) {
        dblink = link.value;
        dblinkRange = rangeFromOffsets(lineIdx, link.start, link.end);
        pos = link.end;
      }
    }

    if (pos < cursor && source[pos] === '.') {
      pos++;
      if (pos === cursor) trailingDot = true;
      continue;
    }
    break;
  }

  if (parts.length === 0) return null;
  return { parts, dblink, dblinkRange, chainStart: start, chainEnd: pos, trailingDot };
}

export function makeQualifiedName(parts: readonly DottedPart[]): QualifiedName {
  const last = parts[parts.length - 1];
  const schema = parts.length >= 2 ? parts[parts.length - 2] : null;
  const database = parts.length >= 3 ? parts[parts.length - 3] : null;
  const qn: QualifiedName = {
    name: last.value,
    foldedName: last.folded,
    quoted: parts.some((p) => p.quoted),
    quoting: last.quoting,
    raw: parts.map((p) => p.raw).join('.'),
  };
  if (schema) {
    qn.schema = schema.value;
    qn.foldedSchema = schema.folded;
  }
  if (database) {
    qn.database = database.value;
    qn.foldedDatabase = database.folded;
  }
  return qn;
}

export function withDblink(rt: ReachableTable, dblink: string, engine: SqlEngine): ReachableTable {
  const folded = engine === 'oracle' ? dblink.toUpperCase() : dblink;
  return { ...rt, qualified: { ...rt.qualified, dblink: folded } };
}
